import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
import { axiosInstance, axiosInstanceEcom, axiosInstanceRDS } from '../utils/axios';
import { PATH_AUTH, PATH_DASHBOARD } from '../routes/paths';
import { isValidToken } from '../utils/jwt';

// ----------------------------------------------------------------------2025

const BASE_URL = process.env.REACT_APP_DASHBOARD_SECURE_API;
// const BASE_URL = 'https://51b2-31-215-210-116.ngrok-free.app'; // NGROK URL

// Function to decode JWT token using jwt-decode package
const decodeJWT = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  userAttr: null,
};

const handlers = {
  AUTHENTICATE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const errorHandler = (err) => {
  let alertMessage = '';
  switch (err.message) {
    case 'Username should be either an email or a phone number.':
      alertMessage = `${err.message}`;
      break;
    case 'Password did not conform with policy: Password not long enough':
      alertMessage = `${err.message}`;
      break;
    case 'User is not confirmed.':
      alertMessage = `${err.message} please check your email for a verification code after you close this alert.`;
      break;
    case 'Incorrect username or password.':
      alertMessage = `${err.message}`;
      break;
    case 'User does not exist.':
      alertMessage = `${err.message} check your credentials and try again.`;
      break;
    case 'User Integration does not exists':
      alertMessage = `${err.message}`;
      break;
    default:
      alertMessage = `${err.message}`;
      break;
  }
  return alertMessage;
};

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verify: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  changeEmail: () => Promise.resolve(),
  forgotPasswordCode: () => Promise.resolve(),
  forgotPasswordReset: () => Promise.resolve(),
  forceChangePassword: () => Promise.resolve(),
  completeNewPassword: () => Promise.resolve(),
  resendVerificationCode: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Function for getting user's role from attributes
  const getRole = (decodedToken) => {
    let role = "default";
    if (decodedToken["custom:is_admin"]) {
      role = "admin";
    } else if (decodedToken["custom:is_accountowner"] === "1") {
      role = "account_owner";
    }
    return role;
  };

  // *Function to get the session if exists / or start a new one (Called and auto login and auth)
  const getSession = useCallback(() => 
    new Promise((resolve, reject) => {
      const token = localStorage.getItem('authToken');
      // console.log('Token from localStorage:', token ? 'Token exists' : 'No token found');
      if (token) {
        try {
          const decodedToken = decodeJWT(token);
          
          if (!decodedToken) {
            console.log('Invalid token format, removing from localStorage');
            localStorage.removeItem('authToken');
            dispatch({
              type: 'AUTHENTICATE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            reject(new Error('Invalid token format'));
            window.location = PATH_AUTH.login;
            return;
          }
          
          // Check if token is expired using the same validation as jwt.js
          if (!isValidToken(token)) {
            console.log('Token expired, removing from localStorage');
            localStorage.removeItem('authToken');
            dispatch({
              type: 'AUTHENTICATE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            reject(new Error('Token expired'));
            window.location = PATH_AUTH.login;
            return;
          }
          
          const role = getRole(decodedToken);
          // console.log('User role:', role);
          
          // Set authorization headers for API requests
          axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
          axiosInstanceRDS.defaults.headers.common.Authorization = `Bearer ${token}`;
          axiosInstanceEcom.defaults.headers.common.Authorization = `Bearer ${token}`;
          
          dispatch({
            type: 'AUTHENTICATE',
            payload: { 
              isAuthenticated: true, 
              user: { ...decodedToken, role, session: { token } } 
            },
          });
          
          resolve({
            token,
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (error) {
          console.error('Error processing token:', error);
          localStorage.removeItem('authToken');
          dispatch({
            type: 'AUTHENTICATE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          reject(error);
          window.location = PATH_AUTH.login;
        }
      } else {
        console.log('No auth token found, user is not authenticated');
        dispatch({
          type: 'AUTHENTICATE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        resolve(null);
      }
    })
  , []);

  // Function on load 
  const initial = useCallback(async () => {
    try {
      await getSession();
      // console.log('Session check completed successfully');
    } catch (error) {
      console.log('Session check failed:', error);
      dispatch({
        type: 'AUTHENTICATE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [getSession]);

  useEffect(() => {
    initial();
  }, [initial]);

  // *Function to login User / Admin - LOGIN PAGE
  const login = useCallback(
    (email, password) =>
      new Promise((resolve, reject) => {
        fetch(`${BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
          }),
        })
          .then(response => response.json())
          .then(data => {
            // console.log('Login response data:', data);
            
            // Check if the user needs to complete new password challenge
            // This happens regardless of the success flag
            if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
              console.log('New password required');
              // Return the session token and challenge info, but don't update authentication state
              resolve({
                ...data,
                requiresNewPassword: true,
                sessionToken: data.session // Using session from the response
              });
              return;
            }
            
            if (!data.success) {
              const error = new Error(data.error || data.message || 'Login failed');
              reject(error);
              return;
            }
            
            if (!data.token) {
              console.error('No token received from server');
              reject(new Error('Authentication failed: No token received'));
              return;
            }
            
            try {
              // Validate token before storing
              if (!isValidToken(data.token)) {
                console.error('Invalid or expired token received from server');
                reject(new Error('Authentication failed: Invalid or expired token'));
                return;
              }
              
              // Store the token
              localStorage.setItem('authToken', data.token);
              
              // Update the authentication state
              getSession();
              resolve(data);
            } catch (error) {
              console.error('Error validating token:', error);
              reject(new Error('Authentication failed: Token validation error'));
            }
          })
          .catch(error => {
            console.error('Error during login:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    [getSession]
  );

  // *Function to register User - REGISTER PAGE (1 - 2)
  const register = useCallback(
    (email, password) =>
      new Promise((resolve, reject) => {
        fetch(`${BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
          }),
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Registration failed');
              reject(error);
              return;
            }
            
            resolve(data);
            window.location.href = PATH_AUTH.verify;
          })
          .catch(error => {
            console.error('Error during registration:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  // *Function to verify User - REGISTER PAGE (2 - 2 only used on verify page)
  const verify = useCallback(
    (email, password, code, skipSession = false) =>
      new Promise((resolve, reject) => {
        // console.log('Verifying and logging in with email:', email);
        // Get existing token if available (might be useful in some scenarios)
        const existingToken = localStorage.getItem('authToken');
        
        fetch(`${BASE_URL}/api/auth/verify-and-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(existingToken && { 'Authorization': `Bearer ${existingToken}` }),
          },
          body: JSON.stringify({
            email,
            password,
            code,
          })
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Verification failed');
              reject(error);
              return;
            }
            
            // Only store token and update session if skipSession is false
            if (!skipSession) {
              // Store the JWT token
              localStorage.setItem('authToken', data.token); 
              
              // Update the authentication state
              getSession(); // this is what caused redirect to Dashboard Home Page / Login in the AuthGuard and authToken is set
            }
            
            resolve({
              jwtToken: data.token,
              // If you need access or refresh tokens too:
              accessToken: data.accessToken,
              refreshToken: data.refreshToken
            });
          })
          .catch(error => {
            console.error('Error during verification:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    [getSession]
  );

  const logout = useCallback(() => {
    console.log('AuthProvider logging out...');
    localStorage.removeItem('authToken');
    
    // Clear authorization headers
    delete axiosInstance.defaults.headers.common.Authorization;
    delete axiosInstanceRDS.defaults.headers.common.Authorization;
    delete axiosInstanceEcom.defaults.headers.common.Authorization;
    
    dispatch({ type: 'LOGOUT' });
  }, []);

  // *Function to change password - ACCOUNT SETTINGS PAGE - DASHBOARD
  const changePassword = useCallback(
    (uuid, email, currentPassword, newPassword) =>
      new Promise((resolve, reject) => {
        const token = localStorage.getItem('authToken');

        if (!token) {
          reject(new Error('No authentication token found'));
          return;
        }
        
        fetch(`${BASE_URL}/api/v1/users/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentPassword,
            newPassword,
          }),
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Password change failed');
              reject(error);
              return;
            }
            
            resolve(data);
          })
          .catch(error => {
            console.error('Error changing password:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  // Function to change email - ACCOUNT SETTINGS PAGE - DASHBOARD
  const changeEmail = useCallback(
    (uuid, newEmail, currentPassword) => 
      new Promise((resolve, reject) => {
        const token = localStorage.getItem('authToken');
        if (!token) {
          reject(new Error('No authentication token found'));
          return;
        }
        
        fetch(`${BASE_URL}/api/v1/users/change-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            newEmail,
            currentPassword,
          }),
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Email change failed');
              reject(error);
              return;
            }
            
            resolve(data);
          })
          .catch(error => {
            console.error('Error changing email:', error);
            reject(new Error(errorHandler(error)));
          });
      })
  ,[]);

  // *Function to send a password reset code part email (1 of 2) - LOGIN PAGE
  const forgotPasswordCode = useCallback(
    (email) =>
      new Promise((resolve, reject) => {
        // console.log('Sending password reset code to:', email);
        fetch(`${BASE_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
          }),
        })
          .then(response => {
            // console.log('Forgot password response status:', response.status);
            if (!response.ok) {
              throw new Error(`Failed to send password reset code (Status: ${response.status})`);
            }
            return response.json();
          })
          .then(data => {
            if (!data.success) {
              const errorMessage = data.error || 'Failed to send password reset code';
              console.error('API error:', errorMessage);
              reject(new Error(errorMessage));
              return;
            }
            
            // console.log('Password reset code sent successfully');
            resolve(data);
          })
          .catch(error => {
            console.error('Error requesting password reset:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  // *Function to reset password part - submit change (2 of 2) - LOGIN PAGE
  const forgotPasswordReset = useCallback(
    (email, code, newPassword) =>
      new Promise((resolve, reject) => {
        // console.log('Resetting password for email:', email, 'with code length:', code.length);
        fetch(`${BASE_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            code,
            newPassword,
          }),
        })
          .then(response => {
            // console.log('Reset password response status:', response.status);
            if (!response.ok) {
              throw new Error(`Password reset failed (Status: ${response.status})`);
            }
            return response.json();
          })
          .then(data => {
            if (!data.success) {
              const errorMessage = data.error || 'Password reset failed';
              console.error('API error:', errorMessage, data);
              reject(new Error(errorMessage));
              return;
            }
            
            // console.log('Password reset successful');
            resolve(data);
          })
          .catch(error => {
            console.error('Error resetting password:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  // *Function to resend verification code - REGISTER PAGE (2 - 2 extra)
  const resendVerificationCode = useCallback(
    (email) =>
      new Promise((resolve, reject) => {
        // console.log('Resending verification code to:', email);
        fetch(`${BASE_URL}/api/auth/resend-verification-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
          }),
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Failed to resend verification code');
              reject(error);
              return;
            }
            
            // console.log('Verification code resent successfully');
            resolve(data);
          })
          .catch(error => {
            console.error('Error resending verification code:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  // *Function to handle completing new password challenge for admin-created users
  const completeNewPassword = useCallback(
    (email, newPassword, sessionToken) =>
      new Promise((resolve, reject) => {
        console.log('Completing new password challenge:', { email, sessionToken: sessionToken ? 'exists' : 'missing' });
        fetch(`${BASE_URL}/api/auth/complete-new-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            newPassword,
            session: sessionToken, // Use the key 'session' to match backend expectation
          }),
        })
          .then(response => response.json())
          .then(data => {
            console.log('Complete new password response:', data);
            if (!data.success) {
              const error = new Error(data.error || data.message || 'Password change failed');
              reject(error);
              return;
            }
            
            if (!data.token) {
              console.error('No token received from server');
              reject(new Error('Authentication failed: No token received'));
              return;
            }
            
            try {
              // Validate and store the token
              if (!isValidToken(data.token)) {
                console.error('Invalid or expired token received from server');
                reject(new Error('Authentication failed: Invalid or expired token'));
                return;
              }
              
              // Store the token
              localStorage.setItem('authToken', data.token);
              
              // Update the authentication state
              getSession();
              resolve(data);
            } catch (error) {
              console.error('Error validating token:', error);
              reject(new Error('Authentication failed: Token validation error'));
            }
          })
          .catch(error => {
            console.error('Error completing new password challenge:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    [getSession]
  );

  const forceChangePassword = useCallback(
    (email, oldPassword, newPassword) =>
      new Promise((resolve, reject) => {
        // For users who need to change their password (created by admin)
        const token = localStorage.getItem('authToken');
        
        fetch(`${BASE_URL}/api/v1/users/force-change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({
            email,
            oldPassword,
            newPassword,
          }),
        })
          .then(response => response.json())
          .then(data => {
            if (!data.success) {
              const error = new Error(data.error || 'Password change failed');
              reject(error);
              return;
            }
            
            resolve(data);
            window.location.href = PATH_DASHBOARD.general.overview;
          })
          .catch(error => {
            console.error('Error changing password:', error);
            reject(new Error(errorHandler(error)));
          });
      }),
    []
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        user: {
          ...state.user,
        },
        login,
        register,
        verify,
        logout,
        changePassword,
        changeEmail,
        forgotPasswordCode,
        forgotPasswordReset,
        forceChangePassword,
        completeNewPassword,
        resendVerificationCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };